/**
 * SigningDomainsView Component
 *
 * Shows which external domains are DKIM-signing email as your domain.
 * Groups by signing domain (dkim_domain) instead of provider, highlighting
 * misaligned domains that represent third parties signing as you.
 */

import { useState, useEffect } from "react";
import {
  Key,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Building2,
  Loader2,
  Globe,
  ArrowUpDown,
  RefreshCw,
} from "lucide-react";
import { api, type SigningDomain } from "@/lib/api";

type SortOption = "status" | "volume" | "pass_rate" | "alpha";

const SORT_LABELS: Record<SortOption, string> = {
  status: "Misaligned first",
  volume: "Volume (high → low)",
  pass_rate: "DKIM pass rate (low → high)",
  alpha: "Alphabetical",
};

function sortDomains(domains: SigningDomain[], sort: SortOption): SigningDomain[] {
  return [...domains].sort((a, b) => {
    switch (sort) {
      case "status":
        if (a.aligned !== b.aligned) return a.aligned ? 1 : -1;
        // Within same alignment, forwarders sort after non-forwarders
        if (!a.aligned && !b.aligned && a.is_forwarder !== b.is_forwarder) return a.is_forwarder ? 1 : -1;
        return b.total_messages - a.total_messages;
      case "volume":
        return b.total_messages - a.total_messages;
      case "pass_rate":
        return a.pass_rate - b.pass_rate;
      case "alpha":
        return a.dkim_domain.localeCompare(b.dkim_domain);
    }
  });
}

interface SigningDomainsViewProps {
  domainId: string;
  domainName: string;
  needsDomainSelection: boolean;
  canView: boolean;
}

export function SigningDomainsView({
  domainId,
  domainName,
  needsDomainSelection,
  canView,
}: SigningDomainsViewProps) {
  const [signingDomains, setSigningDomains] = useState<SigningDomain[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [misalignedCount, setMisalignedCount] = useState(0);
  const [sortBy, setSortBy] = useState<SortOption>("status");
  const [showForwarders, setShowForwarders] = useState(true);

  useEffect(() => {
    if (!domainId || needsDomainSelection || !canView) return;

    let cancelled = false;
    setLoading(true);
    setError(null);

    api.getSigningDomains(domainId).then((data) => {
      if (cancelled) return;
      setSigningDomains(data.signing_domains);
      setMisalignedCount(data.misaligned_count);
      setLoading(false);
    }).catch((err) => {
      if (cancelled) return;
      setError(err.message || "Failed to load signing domains");
      setLoading(false);
    });

    return () => { cancelled = true; };
  }, [domainId, needsDomainSelection, canView]);

  if (!canView) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
        <Key className="w-12 h-12 text-slate-300 mx-auto mb-3" />
        <h3 className="text-lg font-semibold text-slate-800 mb-2">Signing Domain Analysis</h3>
        <p className="text-sm text-slate-500 mb-4">
          See which external domains are signing email as your domain. Requires Starter plan or higher.
        </p>
      </div>
    );
  }

  if (needsDomainSelection) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
        <Globe className="w-12 h-12 text-slate-300 mx-auto mb-3" />
        <h3 className="text-lg font-semibold text-slate-800 mb-2">Select a Domain</h3>
        <p className="text-sm text-slate-500">
          Choose a specific domain from the dropdown above to view signing domain analysis.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
        <Loader2 className="w-8 h-8 text-amber-500 mx-auto mb-3 animate-spin" />
        <p className="text-sm text-slate-500">Analyzing signing domains...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-xl border border-red-200 p-8 text-center">
        <AlertTriangle className="w-12 h-12 text-red-300 mx-auto mb-3" />
        <h3 className="text-lg font-semibold text-slate-800 mb-2">Error</h3>
        <p className="text-sm text-red-600">{error}</p>
      </div>
    );
  }

  if (signingDomains.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
        <Key className="w-12 h-12 text-slate-300 mx-auto mb-3" />
        <h3 className="text-lg font-semibold text-slate-800 mb-2">No Signing Domains Found</h3>
        <p className="text-sm text-slate-500">
          No DKIM signing domain data available for this domain yet.
        </p>
      </div>
    );
  }

  const forwarderCount = signingDomains.filter((sd) => sd.is_forwarder && !sd.aligned).length;
  const filtered = showForwarders ? signingDomains : signingDomains.filter((sd) => !sd.is_forwarder || sd.aligned);
  const sorted = sortDomains(filtered, sortBy);

  return (
    <div className="space-y-4">
      {/* Summary header */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Key className="w-5 h-5 text-slate-600" />
            <div>
              <h3 className="text-sm font-semibold text-slate-800">
                {signingDomains.length} signing domain{signingDomains.length !== 1 ? "s" : ""} detected
              </h3>
              <p className="text-xs text-slate-500">
                Domains that DKIM-sign email with Header From: {domainName}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {misalignedCount > 0 && (
              <div className="flex items-center gap-1.5 px-3 py-1.5 bg-red-50 text-red-700 rounded-lg text-sm font-medium">
                <XCircle className="w-4 h-4" />
                {misalignedCount} misaligned
              </div>
            )}
            {forwarderCount > 0 && (
              <div className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg text-sm font-medium">
                <RefreshCw className="w-3.5 h-3.5" />
                {forwarderCount} forwarder{forwarderCount !== 1 ? "s" : ""}
              </div>
            )}
          </div>
        </div>

        {/* Controls row */}
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100">
          <div className="flex items-center gap-2">
            <ArrowUpDown className="w-3.5 h-3.5 text-slate-400" />
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as SortOption)}
              className="text-xs text-slate-600 bg-transparent border border-slate-200 rounded-md px-2 py-1 focus:outline-none focus:ring-1 focus:ring-amber-400"
            >
              {(Object.entries(SORT_LABELS) as [SortOption, string][]).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>
          {forwarderCount > 0 && (
            <label className="flex items-center gap-1.5 text-xs text-slate-500 cursor-pointer">
              <input
                type="checkbox"
                checked={showForwarders}
                onChange={(e) => setShowForwarders(e.target.checked)}
                className="rounded border-slate-300 text-amber-500 focus:ring-amber-400 w-3.5 h-3.5"
              />
              Show forwarders ({forwarderCount})
            </label>
          )}
        </div>
      </div>

      {/* Signing domain cards */}
      <div className="space-y-2">
        {sorted.map((sd) => (
          <SigningDomainCard key={sd.dkim_domain} signingDomain={sd} domainName={domainName} />
        ))}
      </div>
    </div>
  );
}

function SigningDomainCard({
  signingDomain,
  domainName,
}: {
  signingDomain: SigningDomain;
  domainName: string;
}) {
  const { dkim_domain, aligned, total_messages, pass_rate, ip_count, selectors, providers, is_forwarder } = signingDomain;

  const passRateColor = pass_rate >= 80 ? "text-emerald-600" : pass_rate >= 50 ? "text-amber-600" : "text-red-600";

  // Forwarders that are misaligned get a softer border (expected behavior)
  const borderClass = aligned
    ? "border-slate-200"
    : is_forwarder
      ? "border-blue-200 border-l-4 border-l-blue-400"
      : "border-red-200 border-l-4 border-l-red-500";

  return (
    <div className={`bg-white rounded-xl border overflow-hidden ${borderClass}`}>
      <div className="p-4">
        <div className="flex items-start justify-between gap-4">
          {/* Left: domain info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span className="text-base font-semibold text-slate-900 truncate">
                {dkim_domain}
              </span>
              {aligned ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-50 text-emerald-700 text-xs font-medium rounded-full whitespace-nowrap">
                  <CheckCircle2 className="w-3 h-3" />
                  Aligned
                </span>
              ) : is_forwarder ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-700 text-xs font-medium rounded-full whitespace-nowrap">
                  <RefreshCw className="w-3 h-3" />
                  Forwarder
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-50 text-red-700 text-xs font-medium rounded-full whitespace-nowrap">
                  <XCircle className="w-3 h-3" />
                  Misaligned
                </span>
              )}
            </div>

            {!aligned && !is_forwarder && (
              <p className="text-xs text-red-600 mb-2">
                Signs as <span className="font-medium">{dkim_domain}</span> — Header From: <span className="font-medium">{domainName}</span>
              </p>
            )}

            {!aligned && is_forwarder && (
              <p className="text-xs text-blue-600 mb-2">
                DKIM preserved by forwarder — expected misalignment
              </p>
            )}

            {/* Providers */}
            {providers.length > 0 && (
              <div className="flex items-center gap-1.5 flex-wrap mb-2">
                <Building2 className="w-3.5 h-3.5 text-slate-400" />
                {providers.map((p, i) => (
                  <span
                    key={i}
                    className="inline-flex items-center px-2 py-0.5 bg-slate-100 text-slate-600 text-xs rounded-full"
                  >
                    {p.provider_name}
                  </span>
                ))}
              </div>
            )}

            {/* Selectors */}
            <div className="flex items-center gap-1.5 flex-wrap">
              <Key className="w-3.5 h-3.5 text-slate-400" />
              {selectors.map((sel, i) => (
                <span
                  key={i}
                  className="inline-flex items-center px-2 py-0.5 bg-slate-50 text-slate-500 text-xs rounded font-mono"
                >
                  {sel}
                </span>
              ))}
            </div>
          </div>

          {/* Right: stats */}
          <div className="flex items-center gap-6 text-right shrink-0">
            <div>
              <div className="text-lg font-semibold text-slate-900">
                {total_messages.toLocaleString()}
              </div>
              <div className="text-xs text-slate-500">messages</div>
            </div>
            <div>
              <div className={`text-lg font-semibold ${passRateColor}`}>
                {pass_rate}%
              </div>
              <div className="text-xs text-slate-500">DKIM pass</div>
            </div>
            <div>
              <div className="text-lg font-semibold text-slate-700">
                {ip_count}
              </div>
              <div className="text-xs text-slate-500">IPs</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
